import { useState } from "react";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// ---------------------------------------------------------------------------
// RentToMortgageChart.jsx — NEW COMPONENT
//
// Renders a grouped bar chart comparing:
//   - Cumulative rent paid (sky-500)   — money spent with no return
//   - Cumulative equity gained (emerald-500) — capital repaid + HPI growth
//
// Props:
//   calculatorData  { monthlyRent, interestRate, termYears }
//   results         [estimatedMortgage, minimumIncome, monthlyRepayment, monthlyInterest]
//
// No internal state — all values derived from props on each render.
// Sits below the two-column grid in RentToMortgagePage.jsx.
// ---------------------------------------------------------------------------

// HPI_ANNUAL_RATE — Nationwide House Price Index national average 2020–2025
// Baked in as a constant for MVP. Can be made user-selectable in a future iteration.
const HPI_ANNUAL_RATE = 0.049;

// CHART COLOURS — Tailwind sky-500 and emerald-500 exact hex values
const COLOUR_RENT = "#0ea5e9"; // sky-500
const COLOUR_EQUITY = "#10b981"; // emerald-500

// ---------------------------------------------------------------------------
// buildChartData
// Calculates cumulative rent paid and cumulative equity gained for each year
// from year 1 to termYears.
//
// Equity = principal repaid (amortisation) + HPI appreciation on property value
//
// estimatedMortgage is used as both the loan amount AND the starting property
// value — consistent with how calculateRentToMortgage derives it.
// ---------------------------------------------------------------------------
function buildChartData(
  monthlyRent,
  interestRate,
  termYears,
  estimatedMortgage,
) {
  const monthlyRate = interestRate / 100 / 12;
  const data = [];
  let balance = estimatedMortgage; // outstanding loan balance

  for (let year = 1; year <= termYears; year++) {
    // Run 12 months of amortisation to find principal repaid this year
    for (let m = 0; m < 12; m++) {
      const interestThisMonth = balance * monthlyRate;
      const principalThisMonth =
        monthlyRate === 0
          ? estimatedMortgage / (termYears * 12)
          : monthlyRent - interestThisMonth;
      balance = Math.max(0, balance - principalThisMonth);
    }

    const principalRepaid = estimatedMortgage - balance;
    const hpiGrowth =
      estimatedMortgage * (Math.pow(1 + HPI_ANNUAL_RATE, year) - 1);
    const totalEquity = Math.round(principalRepaid + hpiGrowth);
    const cumulativeRentPaid = Math.round(monthlyRent * 12 * year);

    data.push({
      year,
      // X axis label — show "Year X" every 5th year and year 1; blank otherwise
      // Prevents crowding on longer terms (e.g. 35 years = 35 bars)
      label: year === 1 || year % 5 === 0 ? `Year ${year}` : "",
      rentPaid: cumulativeRentPaid,
      equityGained: totalEquity,
    });
  }

  return data;
}

// ---------------------------------------------------------------------------
// formatGBP — Y axis and tooltip currency formatter
// ---------------------------------------------------------------------------
function formatGBP(value) {
  if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}m`;
  if (value >= 1_000) return `£${(value / 1_000).toFixed(0)}k`;
  return `£${value}`;
}

// ---------------------------------------------------------------------------
// CustomTooltip — shows both values on hover with full £ formatting
// ---------------------------------------------------------------------------
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  const rent = payload.find((p) => p.dataKey === "rentPaid");
  const equity = payload.find((p) => p.dataKey === "equityGained");

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-900">
      <p className="mb-2 font-bold text-black dark:text-white">
        {label || `Year ${payload[0]?.payload?.year}`}
      </p>
      {rent && (
        <p className="text-sm" style={{ color: COLOUR_RENT }}>
          Rent paid: £{rent.value.toLocaleString()}
        </p>
      )}
      {equity && (
        <p className="text-sm" style={{ color: COLOUR_EQUITY }}>
          Equity gained: £{equity.value.toLocaleString()}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RentToMortgageChart — main export
// ---------------------------------------------------------------------------
function RentToMortgageChart({ calculatorData, results }) {
  const { monthlyRent, interestRate, termYears } = calculatorData;
  const estimatedMortgage = results[0];

  // ADDED: temporary demo-only state to let the client hide this card
  // Previously: no visibility state existed — card always rendered when data was valid
  // Resets on page reload since it's plain component state, not persisted anywhere
  const [visible, setVisible] = useState(true);

  // CHANGED: production guard added — this card is a temporary client-demo
  // feature (see "Hide" button below) and must never appear on the live
  // production site, only in dev/preview builds. Previously there was no
  // environment check at all, so the card + Hide button always rendered
  // regardless of NODE_ENV, which is why it was showing in production.
  // Previously: no isProduction check existed here.
  const isProduction = process.env.NODE_ENV === "production";
  if (isProduction) return null;

  // Guard — don't render until we have valid data
  if (!estimatedMortgage || !monthlyRent || !termYears) return null;

  // ADDED: second guard — don't render if hidden via the demo hide button
  if (!visible) return null;

  const data = buildChartData(
    monthlyRent,
    interestRate,
    termYears,
    estimatedMortgage,
  );

  // Headline summary figures — last data point = end of full term
  const finalYear = data[data.length - 1];
  const totalRent = finalYear?.rentPaid ?? 0;
  const totalEquity = finalYear?.equityGained ?? 0;
  const difference = totalEquity - totalRent;

  return (
    <div className="frost relative z-10 w-full rounded-lg border border-gray-200 bg-white p-4 shadow-xl shadow-purple-500/25 dark:border-gray-800 dark:bg-black dark:shadow-purple-500/50">
      {/* CHANGED: mb-8 removed from outer div — grid gap-8 in RentToMortgagePage.jsx
          already provides the row gap above the chart. mb-8 was doubling the space
          between the top cards and the chart vs the gap between the two top cards.
          Previously: <div className="frost relative z-10 mb-8 w-full ..."> */}

      {/* ADDED: temporary "hide" button for client demo purposes, top-right of card. */}
      {/* Previously: no button existed here — remove this block once the client */}
      {/* has decided whether to keep the card permanently. */}
      <button
        onClick={() => setVisible(false)}
        className="absolute right-4 top-4 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
        aria-label="Hide this card"
      >
        Hide
      </button>

      {/* Card heading */}
      <h3 className="mb-1 text-lg font-bold text-black dark:text-white">
        Money Wasted vs Equity Gained
      </h3>
      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        Cumulative rent paid versus equity built through capital repayment and
        estimated house price growth over {termYears} year
        {termYears !== 1 ? "s" : ""}.
      </p>

      {/* Bar chart */}
      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={data}
          margin={{ top: 4, right: 8, left: 8, bottom: 4 }}
          barCategoryGap="20%"
          barGap={2}
        >
          {/* CHANGED: added interval={0} to XAxis
              Previously: <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              Recharts default auto-hides labels it considers overlapping, causing Year 10, 15, 20
              etc to disappear on longer terms. interval={0} forces every tick to render and
              respects our label logic — non-milestone years already return "" so there is no
              visible overlap, just blank ticks between milestones. */}
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            interval={0}
          />
          <YAxis
            tickFormatter={formatGBP}
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            formatter={(value) =>
              value === "rentPaid" ? "Rent Paid" : "Equity Gained"
            }
          />
          <Bar
            dataKey="rentPaid"
            fill={COLOUR_RENT}
            radius={[3, 3, 0, 0]}
            name="rentPaid"
          />
          <Bar
            dataKey="equityGained"
            fill={COLOUR_EQUITY}
            radius={[3, 3, 0, 0]}
            name="equityGained"
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Headline summary figures */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-400 bg-gradient-to-tl from-gray-100 p-3 dark:border-gray-600 dark:from-gray-900">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Total rent paid
          </p>
          <p className="text-lg font-bold" style={{ color: COLOUR_RENT }}>
            £{totalRent.toLocaleString()}
          </p>
        </div>

        <div className="rounded-lg border border-gray-400 bg-gradient-to-tl from-gray-100 p-3 dark:border-gray-600 dark:from-gray-900">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Total equity gained
          </p>
          <p className="text-lg font-bold" style={{ color: COLOUR_EQUITY }}>
            £{totalEquity.toLocaleString()}
          </p>
        </div>

        <div className="rounded-lg border border-gray-400 bg-gradient-to-tl from-gray-100 p-3 dark:border-gray-600 dark:from-gray-900">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {difference >= 0 ? "Better off owning by" : "Renting ahead by"}
          </p>
          <p className="text-lg font-bold text-black dark:text-white">
            £{Math.abs(difference).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="mt-4 text-xs text-gray-400 dark:text-gray-600">
        House price growth based on Nationwide House Price Index national
        average 2020–2025 (~4.9% p.a.). Past performance is not indicative of
        future results. This is for illustrative purposes only and does not
        constitute financial advice.
      </p>
    </div>
  );
}

export default RentToMortgageChart;
